import { isQuantizedFeatureTableAttributes, } from "../../../data/formats/3d_tiles/tiles/pnts";
import { isByteOffset } from "../../../data/formats/3d_tiles/table";
import { numByteSize } from "../../../utils";
import { componentTypeToNumType, numComponentsForVectorType, } from "../../../data/formats/3d_tiles/tiles/pnts/BatchTable";
export function readFeatureTableObject(v) {
    // Parse the feature table JSON and assert we got an object
    const featureTableObject = JSON.parse(v.text);
    const attributes = readFeatureTableAttributes(featureTableObject);
    // Read the mandatory, common feature table fields
    const pointsLength = readSingleNum(v.data, 2 /* NumType.U32 */, featureTableObject.POINTS_LENGTH);
    const common = {
        pointsLength: pointsLength,
    };
    if (typeof featureTableObject.extensions !== "undefined") {
        common.extensions = featureTableObject.extensions;
    }
    if (typeof featureTableObject.extras !== "undefined") {
        common.extras = featureTableObject.extras;
    }
    // Read BATCH_LENGTH
    const batchLength = readSingleNum(v.data, 2 /* NumType.U32 */, featureTableObject.BATCH_LENGTH);
    if (typeof batchLength !== "undefined") {
        common.batchLength = batchLength;
    }
    // Read CONSTANT_RGBA
    const constantRGBA = readMultiNum(v.data, 4, 8 /* NumType.F32 */, featureTableObject.CONSTANT_RGBA);
    if (typeof constantRGBA !== "undefined") {
        common.constantRGBA = [constantRGBA[0], constantRGBA[1], constantRGBA[2], constantRGBA[3]];
    }
    // Read RTC_CENTER
    const rtcCenter = readMultiNum(v.data, 3, 8 /* NumType.F32 */, featureTableObject.RTC_CENTER);
    if (typeof rtcCenter !== "undefined") {
        common.rtcCenter = [rtcCenter[0], rtcCenter[1], rtcCenter[2]];
    }
    // Read in the attributes
    if (isQuantizedFeatureTableAttributes(attributes)) {
        const offset = readMultiNum(v.data, 3, 8 /* NumType.F32 */, featureTableObject.QUANTIZED_VOLUME_OFFSET);
        const scale = readMultiNum(v.data, 3, 8 /* NumType.F32 */, featureTableObject.QUANTIZED_VOLUME_SCALE);
        return Object.assign({ attributes, quantizedVolume: { offset: [offset[0], offset[1], offset[2]], scale: [scale[0], scale[1], scale[2]] } }, common);
    }
    else {
        return Object.assign({ attributes }, common);
    }
}
export function readBatchTableObject(v) {
    // Early exit on no input
    if (typeof v === "undefined") {
        return undefined;
    }
    // Parse the feature table JSON and assert we got an object
    const featureTableObject = JSON.parse(v.text);
    // const attributes = readFeatureTableAttributes(featureTableObject);
    const table = {
        attributes: {},
    };
    for (const [key, item] of Object.entries(featureTableObject).filter(([k, _v]) => k !== "extensions" && k !== "extras")) {
        const value = item;
        if (typeof value.byteOffset === "undefined" || typeof value.byteOffset !== "number") {
            throw new Error("Invalid batch table attribute");
        }
        if (typeof value.componentType === "undefined" || typeof value.componentType !== "string") {
            throw new Error("Invalid batch table attribute");
        }
        if (typeof value.type === "undefined" || typeof value.type !== "string") {
            throw new Error("Invalid batch table attribute");
        }
        table.attributes[key] = {
            byteOffset: value.byteOffset,
            numComponents: numComponentsForVectorType(value.type),
            componentType: componentTypeToNumType(value.componentType),
        };
    }
    if (typeof featureTableObject.extensions !== "undefined") {
        table.extensions = featureTableObject.extensions;
    }
    if (typeof featureTableObject.extras !== "undefined") {
        table.extras = featureTableObject.extras;
    }
    return table;
}
/**
 * Returns
 * @param v
 */
function readFeatureTableAttributes(v) {
    if (typeof v !== "object" || v === null)
        throw new Error();
    let out;
    if (typeof v.POSITION !== "undefined") {
        out = {
            POSITION: v.POSITION,
        };
    }
    else if (typeof v.POSITION_QUANTIZED !== "undefined") {
        out = {
            POSITION_QUANTIZED: v.POSITION_QUANTIZED,
        };
    }
    else {
        throw new Error();
    }
    if (typeof v.RGBA !== "undefined")
        out.RGBA = v.RGBA;
    if (typeof v.RGB !== "undefined")
        out.RGB = v.RGB;
    if (typeof v.RGB565 !== "undefined")
        out.RGB565 = v.RGB565;
    if (typeof v.NORMAL !== "undefined")
        out.NORMAL = v.NORMAL;
    if (typeof v.NORMAL_OCT16P !== "undefined")
        out.NORMAL_OCT16P = v.NORMAL_OCT16P;
    if (typeof v.BATCH_ID !== "undefined")
        out.BATCH_ID = v.BATCH_ID;
    return out;
}
/**
 * Internal function that normalizes the single number representations valid in an on-disk FeatureTable into a number.
 *
 * Will return numbers unchanged.
 *
 * Destructures arrays by only returning the first element.
 *
 * Will read a number of numType from the view if a ByteOffset.
 * @param view
 * @param numType
 * @param v
 */
function readSingleNum(view, numType, v) {
    if (typeof v === "undefined" || typeof v === "number") {
        return v;
    }
    else if (isByteOffset(v)) {
        return readNumFromBlob(view, numType, v.byteOffset);
    }
    else {
        return v[0];
    }
}
/**
 * Internal function that normalizes the various array representations valid in an on-disk FeatureTable into an array.
 *
 * Promotes single numbers to an array of size 1.
 *
 * If an array, returns it verbatim if it matches the required length. Otherwise, returns undefined.
 *
 * If ByteOffset, reads the length numbers of numType from the data view and returns them in an array.
 * @param view
 * @param length
 * @param numType
 * @param v
 */
function readMultiNum(view, length, numType, v) {
    if (typeof v === "undefined") {
        // If we got undefined return it verbatim
        return v;
    }
    else if (typeof v === "number") {
        // If we got a single number wrap it in an array
        return [v];
    }
    else if (isByteOffset(v)) {
        // If we got a data reference read the data from the blob, assuming the data is stored as the provided NumType
        const array = [];
        const byteSize = numByteSize(numType);
        for (let i = 0; i < length; i += 1) {
            const offset = i * byteSize;
            const num = readNumFromBlob(view, numType, v.byteOffset + offset);
            array.push(num);
        }
        return;
    }
    else {
        if (v.length !== length)
            return undefined;
        return v;
    }
}
/**
 * Internal function that switches the get* function used to load from the DataView based on the numType variant.
 * @param view
 * @param numType
 * @param byteOffset
 */
function readNumFromBlob(view, numType, byteOffset) {
    switch (numType) {
        case 3 /* NumType.U64 */:
            throw new Error("U64 can't be represented in a double precision floating point (number)");
        case 7 /* NumType.I64 */:
            throw new Error("I64 can't be represented in a double precision floating point (number)");
        case 0 /* NumType.U8 */:
            return view.getUint8(byteOffset);
        case 4 /* NumType.I8 */:
            return view.getInt8(byteOffset);
        case 1 /* NumType.U16 */:
            return view.getUint16(byteOffset, true);
        case 5 /* NumType.I16 */:
            return view.getInt16(byteOffset, true);
        case 2 /* NumType.U32 */:
            return view.getUint32(byteOffset, true);
        case 6 /* NumType.I32 */:
            return view.getInt32(byteOffset, true);
        case 8 /* NumType.F32 */:
            return view.getFloat32(byteOffset, true);
        case 9 /* NumType.F64 */:
            return view.getFloat64(byteOffset, true);
    }
}
